import { FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { loadSurveyConfig } from "../survey-config";
import { BrandLogo } from "../components/BrandLogo";

async function surveyRequest<T>(
  apiBaseUrl: string,
  path: string,
  init?: RequestInit
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Content-Type") && init?.body) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${apiBaseUrl}${path}`, { ...init, headers });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const j = (await res.json()) as { error?: string; reason?: string };
      if (j.error) message = j.error;
      else if (j.reason) message = j.reason;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export function SurveyPage() {
  const { token } = useParams();
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);
  const [score, setScore] = useState(5);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (!token) return;
      try {
        const config = await loadSurveyConfig();
        const res = await surveyRequest<{
          valid: boolean;
          reason?: string;
          ticketNumber?: string;
        }>(config.apiBaseUrl, `/public/surveys/${token}`);
        if (!res.valid) {
          setErr(res.reason ?? "Invalid link");
          return;
        }
        setTicketNumber(res.ticketNumber ?? null);
      } catch (e) {
        setErr((e as Error).message);
      }
    })();
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    try {
      const config = await loadSurveyConfig();
      await surveyRequest(config.apiBaseUrl, `/public/surveys/${token}`, {
        method: "POST",
        body: JSON.stringify({ score, comment }),
      });
      setDone(true);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-surface-alt">
      <div className="w-full max-w-md card p-6 space-y-4">
        <BrandLogo className="h-8 w-auto" />
        <h1 className="text-xl font-semibold tracking-tight">Rate our support</h1>
        {ticketNumber && (
          <p className="text-sm text-ink-soft">Ticket {ticketNumber}</p>
        )}
        {err && <p className="text-acid-red text-sm">{err}</p>}
        {done ? (
          <p className="text-emerald-700">Thank you for your feedback.</p>
        ) : (
          !err && (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="flex justify-between gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setScore(n)}
                    className={`flex-1 rounded-sm py-3 text-lg font-semibold border ${
                      score === n
                        ? "border-acid-red bg-acid-red/10 text-acid-red"
                        : "border-line hover:border-diagram-grey"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="text-xs text-ink-soft text-center">
                1 = bad · 5 = excellent
              </p>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="Optional comment"
                className="field"
              />
              <button type="submit" className="btn-primary w-full">
                Submit
              </button>
            </form>
          )
        )}
      </div>
    </div>
  );
}
